import { createContext, type PropsWithChildren, use, useEffect, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import Sentry from "@similarweb/sentry-logger";

import { swSettings } from "app/common/services/swSettings";
import { TEAMS } from "app/constants/teamName";
import { DefaultFetchService } from "app/services/fetchService";

import { MESSAGE_TYPE_DICT, useMessagesContext } from "../MessagesContext/MessagesContext";

type IFitScore = "A" | "B" | "C" | "D" | "E" | "F" | "S" | "X";

const ALLOWED_FIT_SCORES: IFitScore[] = ["D", "E"];

const CHILI_PIPER_FIT_SCORES: IFitScore[] = ["A", "B", "C", "D", "E"];

export const HOOK_DATA_KEY = "hookData";
export const HOOK_DATA_SEPARATOR = "!!!";

interface IFitScoreResponse {
    Flow__InterviewGuid: string;
    Flow__InterviewStatus: string;
    Response_FitScore: IFitScore;
    Response_Reason: string;
    Response_Relevent_For_Sales: string;
    Response_is_in_salesforce: boolean;
    Response_is_paying_user: boolean;
    Response_owner_division_from_salesforce?: string;
    Var_Contact_Owner_Title: string;
    Var_Country_Tier: string;
    Var_Marketing_Greenlight: boolean;
}

export interface IFitScoreContext {
    isAllowed: boolean;
    isEvaluated: boolean;
    fitScore?: IFitScoreResponse;
    hasMarketingGreenlight: boolean;
}

const defaultContextValue: IFitScoreContext = {
    isAllowed: false,
    isEvaluated: false,
    hasMarketingGreenlight: false,
};

const url = "/api/fit-score";

const FitScoreContext = createContext<IFitScoreContext>(defaultContextValue);

export const FitScoreProvider = ({ children }: PropsWithChildren) => {
    const { register } = useMessagesContext();

    const {
        isSuccess,
        isError,
        data: fitScore,
    } = useQuery<IFitScoreResponse>({
        queryKey: ["fit-score"],
        queryFn: async () => {
            const fetchService = DefaultFetchService.getInstance();
            try {
                return await fetchService.post<IFitScoreResponse>(url, {});
            } catch (error) {
                Sentry.errorTracker({
                    e: error,
                    message: "Error fetching user fit score",
                    level: "error",
                    params: {
                        tags: { team: TEAMS.GROWTH },
                    },
                });
                throw error;
            }
        },
    });

    const isAllowed = useMemo(() => {
        const division = fitScore?.Response_FitScore;
        return !!(division && ALLOWED_FIT_SCORES.includes(division));
    }, [fitScore]);

    const isEvaluated = isSuccess || isError;

    const hasMarketingGreenlight = useMemo(() => {
        return fitScore?.Var_Marketing_Greenlight ?? false;
    }, [fitScore]);

    useEffect(() => {
        if (register !== null && isEvaluated && fitScore) {
            const ownerDivision = fitScore?.Response_owner_division_from_salesforce ?? "";
            const isOwnedByMirOrVgr = ["MIR", "VGR"].includes(ownerDivision);
            const isChiliPiperAllowedFitScore = CHILI_PIPER_FIT_SCORES.includes(fitScore?.Response_FitScore);

            if (!isOwnedByMirOrVgr || !isChiliPiperAllowedFitScore) {
                return;
            }

            register(MESSAGE_TYPE_DICT.openChiliPiper, () => {
                const chiliPiper = (window as any).ChiliPiper;
                if (chiliPiper) {
                    const hookData = localStorage.getItem(HOOK_DATA_KEY)?.split(HOOK_DATA_SEPARATOR);
                    localStorage.removeItem(HOOK_DATA_KEY);

                    const chilipiper_campaign__c = hookData?.[0];
                    const sections = hookData?.[1].split("/");
                    const utm_content = sections?.shift();
                    const utm_term = sections?.join("/");

                    chiliPiper.submit("similarweb", "cu_router_fro_hooks", {
                        trigger: "InAppButton",
                        lead: {
                            email: swSettings.user.username,
                            utm_source: "Pro",
                            utm_medium: "Hook",
                            utm_term,
                            utm_content,
                            chilipiper_campaign__c,
                        },
                    });
                }
            });
        }
    }, [isEvaluated, fitScore, register]);

    return (
        <FitScoreContext.Provider value={{ isEvaluated, fitScore, isAllowed, hasMarketingGreenlight }}>
            {children}
        </FitScoreContext.Provider>
    );
};

export const useFitScoreContext = () => use(FitScoreContext);
